import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    TextField,
    Grid,
    Tabs,
    Tab,
    Checkbox,
} from '@mui/material';
import { 
    Settings as SettingsIcon, 
    Delete as DeleteIcon,
    Layers as LayersIcon,
    LocalDining as LocalDiningIcon,
    DragIndicator as DragIndicatorIcon,
    CardGiftcard as CardGiftcardIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { memberService } from '../services/memberService';
import { getConfiguration, updateConfiguration } from '../services/coreService';

const ModuleSetup = () => {
    const { userRoles } = useAuth();
    const navigate = useNavigate();
    
    // Tab state
    const [tabValue, setTabValue] = useState(0);
    
    // Tier states
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [tierToDelete, setTierToDelete] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    
    // Form state for adding new tier
    const [newTier, setNewTier] = useState({
        points: '',
        tierName: '',
    });
    const [formErrors, setFormErrors] = useState({});
    
    // Member Points Setup state
    const [pointsValue, setPointsValue] = useState('');
    const [pointsValueLoading, setPointsValueLoading] = useState(true);
    const [pointsValueSaving, setPointsValueSaving] = useState(false);
    
    // Application ID for Membership Platform
    const MEMBERSHIP_APP_ID = 2;

    // Allergy states
    const [allergies, setAllergies] = useState([]);
    const [allergyLoading, setAllergyLoading] = useState(true);
    const [allergyDeleteDialogOpen, setAllergyDeleteDialogOpen] = useState(false);
    const [allergyToDelete, setAllergyToDelete] = useState(null);
    const [allergySubmitting, setAllergySubmitting] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    
    // Form state for adding new allergy
    const [newAllergy, setNewAllergy] = useState({
        name: '',
    });
    const [allergyFormErrors, setAllergyFormErrors] = useState({});

    // Reward states
    const [rewards, setRewards] = useState([]);
    const [rewardLoading, setRewardLoading] = useState(true);
    const [rewardDeleteDialogOpen, setRewardDeleteDialogOpen] = useState(false);
    const [rewardToDelete, setRewardToDelete] = useState(null);
    const [rewardSubmitting, setRewardSubmitting] = useState(false);
    const [rewardDraggedIndex, setRewardDraggedIndex] = useState(null);
    
    // Form state for adding new reward
    const [newReward, setNewReward] = useState({
        name: '',
        pointsCost: 0,
    });
    const [rewardFormErrors, setRewardFormErrors] = useState({});

    // Check if user has required roles
    const allowedRoles = ['Support Super User', 'Administrator', 'Store Manager'];
    const hasAccess = userRoles.some(role => allowedRoles.includes(role.RoleName));

    useEffect(() => {
        // Redirect if user doesn't have access
        if (!hasAccess && !loading) {
            navigate('/permission-denied');
            return;
        }

        if (hasAccess) {
            fetchTiers();
            fetchAllergies();
            fetchRewards();
            fetchPointsValue();
        }
    }, [hasAccess, navigate]);

    const fetchTiers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await memberService.getMemberTiers();
            setTiers(data.tiers || []);
        } catch (err) {
            console.error('Error fetching member tiers:', err);
            setError('Failed to load member tiers. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (tier) => {
        setTierToDelete(tier);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setError(null);
            setSuccessMessage('');
            
            await memberService.deleteMemberTier(tierToDelete.Points);
            
            setSuccessMessage('Tier deleted successfully');
            setDeleteDialogOpen(false);
            setTierToDelete(null);
            
            // Refresh the tier list
            fetchTiers();
        } catch (err) {
            console.error('Error deleting tier:', err);
            setError(err.response?.data?.error || 'Failed to delete tier. Please try again.');
            setDeleteDialogOpen(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setTierToDelete(null);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleNewTierChange = (field) => (event) => {
        setNewTier(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        
        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateTierForm = () => {
        const errors = {};
        
        if (!newTier.points || newTier.points === '') {
            errors.points = 'Points is required';
        } else if (isNaN(newTier.points) || parseInt(newTier.points) < 0) {
            errors.points = 'Points must be a valid number (0 or greater)';
        }
        
        if (!newTier.tierName.trim()) {
            errors.tierName = 'Tier Name is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddTier = async () => {
        if (!validateTierForm()) {
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            setSuccessMessage('');

            const response = await memberService.createMemberTier({
                points: parseInt(newTier.points),
                tierName: newTier.tierName
            });

            if (response.success) {
                setSuccessMessage('Tier added successfully');
                
                // Clear form
                setNewTier({
                    points: '',
                    tierName: '',
                });
                
                // Refresh tier list
                fetchTiers();
            } else {
                setError(response.error || 'Failed to add tier');
            }
        } catch (err) {
            console.error('Error adding tier:', err);
            setError(err.response?.data?.error || 'Failed to add tier. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const fetchPointsValue = async () => {
        try {
            setPointsValueLoading(true);
            setError(null);
            
            const configData = await getConfiguration(MEMBERSHIP_APP_ID, 'AmountPerPoint');
            
            if (configData && configData.length > 0) {
                // Configuration exists, parse and display
                const value = parseFloat(configData[0].ConfigValue);
                setPointsValue(value.toString());
            } else {
                // Configuration not found, create with default value "1"
                await updateConfiguration(MEMBERSHIP_APP_ID, 'AmountPerPoint', '1');
                setPointsValue('1');
            }
        } catch (err) {
            console.error('Error fetching points value:', err);
            // If error occurs, try to create default value
            try {
                await updateConfiguration(MEMBERSHIP_APP_ID, 'AmountPerPoint', '1');
                setPointsValue('1');
            } catch (createErr) {
                console.error('Error creating default points value:', createErr);
                setError('Failed to load points configuration. Please try again.');
            }
        } finally {
            setPointsValueLoading(false);
        }
    };

    const handlePointsValueChange = (event) => {
        setPointsValue(event.target.value);
    };

    const handleSavePointsValue = async () => {
        try {
            setPointsValueSaving(true);
            setError(null);
            setSuccessMessage('');
            
            // Validate input
            const numValue = parseFloat(pointsValue);
            if (isNaN(numValue) || numValue <= 0) {
                setError('Please enter a valid positive number for points value');
                return;
            }
            
            // Update configuration
            await updateConfiguration(MEMBERSHIP_APP_ID, 'AmountPerPoint', pointsValue);
            
            setSuccessMessage('Points value saved successfully');
        } catch (err) {
            console.error('Error saving points value:', err);
            setError(err.response?.data?.error || 'Failed to save points value. Please try again.');
        } finally {
            setPointsValueSaving(false);
        }
    };

    // Allergy functions
    const fetchAllergies = async () => {
        try {
            setAllergyLoading(true);
            const data = await memberService.getAllergies();
            setAllergies(data.allergies || []);
        } catch (err) {
            console.error('Error fetching allergies:', err);
            setError('Failed to load allergies. Please try again.');
        } finally {
            setAllergyLoading(false);
        }
    };

    const handleAllergyDeleteClick = (allergy) => {
        setAllergyToDelete(allergy);
        setAllergyDeleteDialogOpen(true);
    };

    const handleAllergyDeleteConfirm = async () => {
        try {
            setError(null);
            setSuccessMessage('');
            
            // Remove the allergy from the list and update display order
            const updatedAllergies = allergies
                .filter(a => a.Name !== allergyToDelete.Name)
                .map((a, index) => ({
                    Name: a.Name,
                    DisplayOrder: index + 1
                }));
            
            await memberService.updateAllergyOrder(updatedAllergies);
            
            setSuccessMessage('Allergy deleted successfully');
            setAllergyDeleteDialogOpen(false);
            setAllergyToDelete(null);
            
            // Refresh the allergy list
            fetchAllergies();
        } catch (err) {
            console.error('Error deleting allergy:', err);
            setError(err.response?.data?.error || 'Failed to delete allergy. Please try again.');
            setAllergyDeleteDialogOpen(false);
        }
    };

    const handleAllergyDeleteCancel = () => {
        setAllergyDeleteDialogOpen(false);
        setAllergyToDelete(null);
    };

    const handleNewAllergyChange = (event) => {
        setNewAllergy({ name: event.target.value });
        
        // Clear error when user starts typing
        if (allergyFormErrors.name) {
            setAllergyFormErrors({});
        }
    };

    const validateAllergyForm = () => {
        const errors = {};
        
        if (!newAllergy.name.trim()) {
            errors.name = 'Allergy name is required';
        }
        
        setAllergyFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddAllergy = async () => {
        if (!validateAllergyForm()) {
            return;
        }

        try {
            setAllergySubmitting(true);
            setError(null);
            setSuccessMessage('');

            const response = await memberService.createAllergy({
                name: newAllergy.name
            });

            if (response.success) {
                setSuccessMessage('Allergy added successfully');
                
                // Clear form
                setNewAllergy({ name: '' });
                
                // Refresh allergy list
                fetchAllergies();
            } else {
                setError(response.error || 'Failed to add allergy');
            }
        } catch (err) {
            console.error('Error adding allergy:', err);
            setError(err.response?.data?.error || 'Failed to add allergy. Please try again.');
        } finally {
            setAllergySubmitting(false);
        }
    };

    // Drag and drop handlers for allergies
    const handleDragStart = (e, index) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
        setDraggedIndex(null);

        if (dragIndex === dropIndex) return;

        // Reorder the allergies array
        const reorderedAllergies = [...allergies];
        const [draggedItem] = reorderedAllergies.splice(dragIndex, 1);
        reorderedAllergies.splice(dropIndex, 0, draggedItem);

        // Update display order
        const updatedAllergies = reorderedAllergies.map((allergy, index) => ({
            Name: allergy.Name,
            DisplayOrder: index + 1
        }));

        // Optimistically update UI
        setAllergies(reorderedAllergies);

        // Save to database
        try {
            await memberService.updateAllergyOrder(updatedAllergies);
            setSuccessMessage('Allergy order updated successfully');
        } catch (err) {
            console.error('Error updating allergy order:', err);
            setError(err.response?.data?.error || 'Failed to update allergy order. Please try again.');
            // Refresh to get correct order from database
            fetchAllergies();
        }
    };

    // Reward functions
    const fetchRewards = async () => {
        try {
            setRewardLoading(true);
            const data = await memberService.getRewards(false); // Get all rewards
            setRewards(data.rewards || []);
        } catch (err) {
            console.error('Error fetching rewards:', err);
            setError('Failed to load rewards. Please try again.');
        } finally {
            setRewardLoading(false);
        }
    };

    const handleRewardDeleteClick = (reward) => {
        setRewardToDelete(reward);
        setRewardDeleteDialogOpen(true);
    };

    const handleRewardDeleteConfirm = async () => {
        try {
            setError(null);
            setSuccessMessage('');
            
            // Remove the reward from the list and update display order
            const updatedRewards = rewards
                .filter(r => r.Name !== rewardToDelete.Name)
                .map((r, index) => ({
                    Name: r.Name,
                    DisplayOrder: index + 1,
                    IsActive: r.IsActive,
                    PointsCost: r.PointsCost ?? 0,
                }));
            
            await memberService.updateRewardOrder(updatedRewards);
            
            setSuccessMessage('Reward deleted successfully');
            setRewardDeleteDialogOpen(false);
            setRewardToDelete(null);
            
            // Refresh the reward list
            fetchRewards();
        } catch (err) {
            console.error('Error deleting reward:', err);
            setError(err.response?.data?.error || 'Failed to delete reward. Please try again.');
            setRewardDeleteDialogOpen(false);
        }
    };

    const handleRewardDeleteCancel = () => {
        setRewardDeleteDialogOpen(false);
        setRewardToDelete(null);
    };

    const handleNewRewardChange = (event) => {
        const { name, value } = event.target;
        if (name === 'pointsCost') {
            const parsed = value === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
            setNewReward(prev => ({ ...prev, pointsCost: parsed }));
        } else {
            setNewReward(prev => ({ ...prev, name: value }));
        }
        if (rewardFormErrors.name || rewardFormErrors.pointsCost) {
            setRewardFormErrors({});
        }
    };

    const validateRewardForm = () => {
        const errors = {};
        if (!newReward.name.trim()) {
            errors.name = 'Reward name is required';
        }
        const pointsCost = typeof newReward.pointsCost === 'number' ? newReward.pointsCost : (parseInt(String(newReward.pointsCost), 10) || 0);
        if (pointsCost < 0 || !Number.isInteger(pointsCost)) {
            errors.pointsCost = 'Points cost must be a non-negative integer';
        }
        setRewardFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const parsePointsCost = (v) => Math.max(0, parseInt(String(v), 10) || 0);

    const handleAddReward = async () => {
        if (!validateRewardForm()) {
            return;
        }

        try {
            setRewardSubmitting(true);
            setError(null);
            setSuccessMessage('');

            const pointsCost = parsePointsCost(newReward.pointsCost);
            const response = await memberService.createReward({
                name: newReward.name,
                pointsCost,
            });

            if (response.success) {
                setSuccessMessage('Reward added successfully');
                setNewReward({ name: '', pointsCost: 0 });
                fetchRewards();
            } else {
                setError(response.error || 'Failed to add reward');
            }
        } catch (err) {
            console.error('Error adding reward:', err);
            setError(err.response?.data?.error || 'Failed to add reward. Please try again.');
        } finally {
            setRewardSubmitting(false);
        }
    };

    // Drag and drop handlers for rewards
    const handleRewardDragStart = (e, index) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index);
        setRewardDraggedIndex(index);
    };

    const handleRewardDragEnd = () => {
        setRewardDraggedIndex(null);
    };

    const handleRewardDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleRewardDrop = async (e, dropIndex) => {
        e.preventDefault();
        const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
        setRewardDraggedIndex(null);

        if (dragIndex === dropIndex) return;

        // Reorder the rewards array
        const reorderedRewards = [...rewards];
        const [draggedItem] = reorderedRewards.splice(dragIndex, 1);
        reorderedRewards.splice(dropIndex, 0, draggedItem);

        // Update display order
        const updatedRewards = reorderedRewards.map((reward, index) => ({
            Name: reward.Name,
            DisplayOrder: index + 1,
            IsActive: reward.IsActive,
            PointsCost: reward.PointsCost ?? 0,
        }));

        // Optimistically update UI
        setRewards(reorderedRewards);

        // Save to database
        try {
            await memberService.updateRewardOrder(updatedRewards);
            setSuccessMessage('Reward order updated successfully');
        } catch (err) {
            console.error('Error updating reward order:', err);
            setError(err.response?.data?.error || 'Failed to update reward order. Please try again.');
            // Refresh to get correct order from database
            fetchRewards();
        }
    };

    const handleRewardPointsCostChange = async (reward, rawValue) => {
        const pointsCost = parsePointsCost(rawValue);
        const updatedRewards = rewards.map(r =>
            r.Name === reward.Name ? { ...r, PointsCost: pointsCost } : { ...r, PointsCost: r.PointsCost ?? 0 }
        );
        const payload = updatedRewards.map((r, i) => ({
            Name: r.Name,
            DisplayOrder: r.DisplayOrder ?? i + 1,
            IsActive: r.IsActive,
            PointsCost: r.PointsCost ?? 0,
        }));

        try {
            setError(null);
            setSuccessMessage('');
            setRewards(prev => prev.map(r => (r.Name === reward.Name ? { ...r, PointsCost: pointsCost } : r)));
            await memberService.updateRewardOrder(payload);
            setSuccessMessage('Points cost updated');
        } catch (err) {
            console.error('Error updating points cost:', err);
            setError(err.response?.data?.error || 'Failed to update points cost.');
            fetchRewards();
        }
    };

    const handleRewardActiveToggle = async (reward) => {
        try {
            setError(null);
            setSuccessMessage('');
            
            // Update the rewards list with toggled IsActive
            const updatedRewards = rewards.map(r => ({
                Name: r.Name,
                DisplayOrder: r.DisplayOrder,
                IsActive: r.Name === reward.Name ? !r.IsActive : r.IsActive,
                PointsCost: r.PointsCost ?? 0,
            }));
            
            // Optimistically update UI
            setRewards(prevRewards => 
                prevRewards.map(r => 
                    r.Name === reward.Name 
                        ? { ...r, IsActive: !r.IsActive }
                        : r
                )
            );
            
            await memberService.updateRewardOrder(updatedRewards);
            setSuccessMessage('Reward status updated successfully');
        } catch (err) {
            console.error('Error updating reward status:', err);
            setError(err.response?.data?.error || 'Failed to update reward status. Please try again.');
            // Refresh to get correct state from database
            fetchRewards();
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!hasAccess) {
        return null; // Will redirect in useEffect
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="primary" />
                Module Setup
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    aria-label="module setup tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                >
                    <Tab 
                        label="Tier Setup" 
                        icon={<LayersIcon />} 
                        iconPosition="start"
                    />
                    <Tab 
                        label="Allergy List" 
                        icon={<LocalDiningIcon />} 
                        iconPosition="start"
                    />
                    <Tab 
                        label="Reward" 
                        icon={<CardGiftcardIcon />} 
                        iconPosition="start"
                    />
                </Tabs>
            </Paper>

            {/* Tab 0: Member Tier Setup Section */}
            {tabValue === 0 && (
            <Box sx={{ mt: 3 }}>
                {/* Member Points Setup Section */}
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                        Member Points Setup
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    {pointsValueLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: 500 }}>
                            <Typography variant="body1" sx={{ minWidth: 'fit-content' }}>
                                1 point = $
                            </Typography>
                            <TextField
                                size="small"
                                type="number"
                                value={pointsValue}
                                onChange={handlePointsValueChange}
                                disabled={pointsValueSaving}
                                InputProps={{
                                    inputProps: { min: 0, step: 0.01 }
                                }}
                                sx={{ flexGrow: 1 }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSavePointsValue}
                                disabled={pointsValueSaving || !pointsValue}
                            >
                                {pointsValueSaving ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Saving...
                                    </>
                                ) : (
                                    'Save'
                                )}
                            </Button>
                        </Box>
                    )}
                </Paper>

                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                    Member Tier Setup
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>
                    {/* Left Side - Tier Table */}
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ overflow: 'hidden' }}>
                            <TableContainer sx={{ maxHeight: 500 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width="120">Points</TableCell>
                                            <TableCell width="240">Tier Name</TableCell>
                                            <TableCell align="center" width="100">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tiers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No tiers found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            tiers.map((tier) => (
                                                <TableRow 
                                                    key={tier.Points}
                                                    hover
                                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                >
                                                    <TableCell>{tier.Points}</TableCell>
                                                    <TableCell>{tier.TierName}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleDeleteClick(tier)}
                                                            title="Delete tier"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Tiers: {tiers.length}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Right Side - Add Tier Form */}
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                Add New Tier
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Points"
                                    type="number"
                                    value={newTier.points}
                                    onChange={handleNewTierChange('points')}
                                    error={!!formErrors.points}
                                    helperText={formErrors.points}
                                    InputProps={{
                                        inputProps: { min: 0 }
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="Tier Name"
                                    value={newTier.tierName}
                                    onChange={handleNewTierChange('tierName')}
                                    error={!!formErrors.tierName}
                                    helperText={formErrors.tierName}
                                />

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleAddTier}
                                    disabled={submitting || !newTier.points || !newTier.tierName}
                                    sx={{ mt: 1 }}
                                >
                                    {submitting ? (
                                        <>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Adding...
                                        </>
                                    ) : (
                                        'Add Tier'
                                    )}
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
            )}

            {/* Tab 1: Allergy List Section */}
            {tabValue === 1 && (
            <Box sx={{ mt: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                    Allergy List
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Drag and drop rows to reorder allergies
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>
                    {/* Left Side - Allergy Table */}
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ overflow: 'hidden' }}>
                            <TableContainer sx={{ maxHeight: 500 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width="50"></TableCell>
                                            <TableCell>Allergy Name</TableCell>
                                            <TableCell align="center" width="100">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {allergyLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <CircularProgress size={24} />
                                                </TableCell>
                                            </TableRow>
                                        ) : allergies.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No allergies found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            allergies.map((allergy, index) => (
                                                <TableRow 
                                                    key={allergy.Name}
                                                    hover
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, index)}
                                                    onDragEnd={handleDragEnd}
                                                    onDragOver={handleDragOver}
                                                    onDrop={(e) => handleDrop(e, index)}
                                                    sx={{ 
                                                        '&:last-child td, &:last-child th': { border: 0 },
                                                        cursor: 'move',
                                                        opacity: draggedIndex === index ? 0.5 : 1,
                                                        bgcolor: draggedIndex === index ? 'action.selected' : 'inherit',
                                                        '&:hover': {
                                                            bgcolor: draggedIndex === index ? 'action.selected' : 'action.hover'
                                                        }
                                                    }}
                                                >
                                                    <TableCell>
                                                        <DragIndicatorIcon 
                                                            sx={{ 
                                                                color: 'text.secondary',
                                                                cursor: 'grab',
                                                                '&:active': { cursor: 'grabbing' }
                                                            }} 
                                                        />
                                                    </TableCell>
                                                    <TableCell>{allergy.Name}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleAllergyDeleteClick(allergy)}
                                                            title="Delete allergy"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Allergies: {allergies.length}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Right Side - Add Allergy Form */}
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                Add New Allergy
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Allergy Name"
                                    value={newAllergy.name}
                                    onChange={handleNewAllergyChange}
                                    error={!!allergyFormErrors.name}
                                    helperText={allergyFormErrors.name}
                                />

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleAddAllergy}
                                    disabled={allergySubmitting || !newAllergy.name}
                                    sx={{ mt: 1 }}
                                >
                                    {allergySubmitting ? (
                                        <>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Adding...
                                        </>
                                    ) : (
                                        'Add Allergy'
                                    )}
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
            )}

            {/* Tab 2: Reward Section */}
            {tabValue === 2 && (
            <Box sx={{ mt: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                    Reward List
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Drag and drop rows to reorder rewards. Check/uncheck to toggle active status.
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>
                    {/* Left Side - Reward Table */}
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ overflow: 'hidden' }}>
                            <TableContainer sx={{ maxHeight: 500 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width="50"></TableCell>
                                            <TableCell>Reward Name</TableCell>
                                            <TableCell align="center" width="120">Points Cost</TableCell>
                                            <TableCell align="center" width="100">Active</TableCell>
                                            <TableCell align="center" width="100">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rewardLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <CircularProgress size={24} />
                                                </TableCell>
                                            </TableRow>
                                        ) : rewards.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No rewards found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            rewards.map((reward, index) => (
                                                <TableRow 
                                                    key={reward.Name}
                                                    hover
                                                    draggable
                                                    onDragStart={(e) => handleRewardDragStart(e, index)}
                                                    onDragEnd={handleRewardDragEnd}
                                                    onDragOver={handleRewardDragOver}
                                                    onDrop={(e) => handleRewardDrop(e, index)}
                                                    sx={{ 
                                                        '&:last-child td, &:last-child th': { border: 0 },
                                                        cursor: 'move',
                                                        opacity: rewardDraggedIndex === index ? 0.5 : 1,
                                                        bgcolor: rewardDraggedIndex === index ? 'action.selected' : 'inherit',
                                                        '&:hover': {
                                                            bgcolor: rewardDraggedIndex === index ? 'action.selected' : 'action.hover'
                                                        }
                                                    }}
                                                >
                                                    <TableCell>
                                                        <DragIndicatorIcon 
                                                            sx={{ 
                                                                color: 'text.secondary',
                                                                cursor: 'grab',
                                                                '&:active': { cursor: 'grabbing' }
                                                            }} 
                                                        />
                                                    </TableCell>
                                                    <TableCell>{reward.Name}</TableCell>
                                                    <TableCell
                                                        align="center"
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={reward.PointsCost ?? 0}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                setRewards(prev => prev.map(r =>
                                                                    r.Name === reward.Name
                                                                        ? { ...r, PointsCost: v === '' ? 0 : Math.max(0, parseInt(v, 10) || 0) }
                                                                        : r
                                                                ));
                                                            }}
                                                            onBlur={(e) => handleRewardPointsCostChange(reward, e.target.value)}
                                                            inputProps={{ min: 0, step: 1, 'aria-label': 'Points cost' }}
                                                            sx={{ width: 110, '& .MuiInputBase-input': { textAlign: 'center' } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Checkbox
                                                            checked={reward.IsActive}
                                                            onChange={() => handleRewardActiveToggle(reward)}
                                                            color="primary"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleRewardDeleteClick(reward)}
                                                            title="Delete reward"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Rewards: {rewards.length}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Right Side - Add Reward Form */}
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                Add New Reward
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    fullWidth
                                    name="name"
                                    label="Reward Name"
                                    value={newReward.name}
                                    onChange={handleNewRewardChange}
                                    error={!!rewardFormErrors.name}
                                    helperText={rewardFormErrors.name}
                                />
                                <TextField
                                    name="pointsCost"
                                    type="number"
                                    label="Points Cost"
                                    value={newReward.pointsCost}
                                    onChange={handleNewRewardChange}
                                    error={!!rewardFormErrors.pointsCost}
                                    helperText={rewardFormErrors.pointsCost}
                                    inputProps={{ min: 0, step: 1 }}
                                />

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleAddReward}
                                    disabled={rewardSubmitting || !newReward.name}
                                    sx={{ mt: 1 }}
                                >
                                    {rewardSubmitting ? (
                                        <>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Adding...
                                        </>
                                    ) : (
                                        'Add Reward'
                                    )}
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
            )}

            {/* Tier Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete tier "{tierToDelete?.TierName}" with {tierToDelete?.Points} points?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Allergy Delete Confirmation Dialog */}
            <Dialog
                open={allergyDeleteDialogOpen}
                onClose={handleAllergyDeleteCancel}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete allergy "{allergyToDelete?.Name}"?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAllergyDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleAllergyDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reward Delete Confirmation Dialog */}
            <Dialog
                open={rewardDeleteDialogOpen}
                onClose={handleRewardDeleteCancel}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete reward "{rewardToDelete?.Name}"?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRewardDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleRewardDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ModuleSetup;

